import type { DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { isActivityStuck, stuckDays, type ActivityType } from "@torre/work-hours";
import type { ThemeClasses } from "../../../types/components";
import type { ActivityRow } from "../services/activitiesService";
import { PRIORITY_ACCENT, PRIORITY_ICON, PRIORITY_TONE, activityLabels, formatMinutes, formatMoney, githubUrl, initials, isOverdue, shortDate, typeOf } from "../lib/format";
import PersonAvatar from "../../../components/staff-ops/PersonAvatar";
import { canShowStaffCosts } from "../../../config/platformFeatures";

// ---------------------------------------------------------------------------
// Tarjeta de un ticket (Kanban y backlog). El costo SOLO se pinta si el
// servidor lo mandó (`cost`, dueños): quien no es dueño ni siquiera recibe el
// campo (épica #1299).
// ---------------------------------------------------------------------------

interface Props {
  activity: ActivityRow;
  catalog: readonly ActivityType[];
  isOwner: boolean;
  /** Título de la épica padre, si se conoce. */
  parentTitle?: string | null;
  tc: ThemeClasses;
  onOpen: (activity: ActivityRow) => void;
  draggable?: boolean;
  onDragStart?: (e: DragEvent<HTMLDivElement>, activity: ActivityRow) => void;
  /** Modo compacto (backlog): una línea. */
  compact?: boolean;
  selected?: boolean;
  onToggleSelect?: (activity: ActivityRow) => void;
}

export default function ActivityCard({ activity: a, catalog, isOwner, parentTitle, tc, onOpen, draggable = false, onDragStart, compact = false, selected = false, onToggleSelect }: Props) {
  const { t } = useTranslation("common");
  const type = typeOf(catalog, a.typeKey);
  const now = new Date().toISOString();
  const stuck = isActivityStuck(a, now);
  const overdue = isOverdue(a);
  const gh = githubUrl(a);

  const keyEl = gh ? (
    <a href={gh} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="font-mono text-[11px] text-violet-500 hover:underline" title={t("staffActivities.card.openGithub")}>
      <i className="ri-github-fill mr-0.5" />
      {a.key}
    </a>
  ) : (
    <span className={`font-mono text-[11px] ${tc.textSecondary}`}>{a.key}</span>
  );

  // Sin costos donde Nómina/Staff están apagadas (WAYAK): regla de nómina.
  const cost = isOwner && a.cost && canShowStaffCosts() ? a.cost : null;
  // #1595: etiquetas propias o las del issue de GitHub, y avance contra lo estimado.
  const labels = activityLabels(a);
  const progress = a.estimateMinutes ? Math.min(1, (Number(a.loggedMinutes) || 0) / a.estimateMinutes) : null;

  if (compact) {
    return (
      <div
        data-testid={`activity-row-${a.id}`}
        className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${tc.cardBorder} ${tc.cardBg} ${selected ? "ring-2 ring-violet-500" : ""}`}
      >
        {onToggleSelect && (
          <input type="checkbox" aria-label={a.title} checked={selected} onChange={() => onToggleSelect(a)} className="h-4 w-4 accent-violet-500" />
        )}
        <i className={`${PRIORITY_ICON[a.priority] ?? "ri-subtract-line"} ${PRIORITY_TONE[a.priority] ?? ""}`} title={t(`staffActivities.priority.${a.priority}`)} />
        <i className={`${type.icon} text-violet-500`} title={type.name} />
        <button type="button" onClick={() => onOpen(a)} className={`min-w-0 flex-1 truncate text-left text-sm ${tc.text} hover:underline`}>
          {a.title}
        </button>
        {keyEl}
        {a.storyPoints != null && <span className={`text-[11px] ${tc.textSecondary}`}>{a.storyPoints} {t("staffActivities.card.points")}</span>}
        {a.dueDate && <span className={`text-[11px] ${overdue ? "text-red-500" : tc.textSecondary}`}>{shortDate(a.dueDate)}</span>}
        {a.assigneeName ? (
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-violet-500/15 text-[10px] font-semibold text-violet-600" title={a.assigneeName}>
            {initials(a.assigneeName)}
          </span>
        ) : (
          <span className={`text-[11px] italic ${tc.textSecondary}`}>{t("staffActivities.kanban.unassigned")}</span>
        )}
        {cost && (
          <span className="text-[11px] text-emerald-600" data-testid={`activity-cost-${a.id}`}>
            {formatMoney(cost.estimated ?? cost.actual, cost.currency)}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`activity-card-${a.id}`}
      draggable={draggable}
      onDragStart={(e) => onDragStart?.(e, a)}
      onClick={() => onOpen(a)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen(a)}
      className={`cursor-pointer rounded-xl border border-l-4 p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${tc.cardBorder} ${PRIORITY_ACCENT[a.priority] ?? ""} ${tc.cardBg} ${draggable ? "active:cursor-grabbing" : ""}`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="flex items-center gap-1.5">
          <i className={`${type.icon} text-violet-500`} title={type.name} />
          {keyEl}
        </span>
        <i className={`${PRIORITY_ICON[a.priority] ?? "ri-subtract-line"} ${PRIORITY_TONE[a.priority] ?? ""}`} title={t(`staffActivities.priority.${a.priority}`)} />
      </div>
      <p className={`mt-1 text-sm font-medium leading-snug ${tc.text}`}>{a.title}</p>
      {(parentTitle || a.isEpic) && (
        <p className="mt-1 truncate text-[11px] text-fuchsia-600" title={parentTitle ?? undefined}>
          <i className="ri-flag-2-line mr-1" />
          {a.isEpic ? t("staffActivities.card.epic") : parentTitle}
        </p>
      )}
      {labels.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1" data-testid={`activity-labels-${a.id}`}>
          {labels.slice(0, 4).map((l) => (
            <span key={l} className="rounded-full bg-subtle px-1.5 py-0.5 text-[10px] text-content-secondary">
              #{l}
            </span>
          ))}
        </div>
      )}
      <div className={`mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] ${tc.textSecondary}`}>
        {a.storyPoints != null && (
          <span>
            <i className="ri-hashtag mr-0.5" />
            {a.storyPoints} {t("staffActivities.card.points")}
          </span>
        )}
        {a.estimateMinutes != null && (
          <span title={t("staffActivities.card.estimate")}>
            <i className="ri-time-line mr-0.5" />
            {formatMinutes(a.loggedMinutes)} / {formatMinutes(a.estimateMinutes)}
          </span>
        )}
        {a.estimateMinutes == null && a.loggedMinutes > 0 && (
          <span title={t("staffActivities.card.logged")}>
            <i className="ri-time-line mr-0.5" />
            {formatMinutes(a.loggedMinutes)}
          </span>
        )}
        {a.dueDate && (
          <span className={overdue ? "font-semibold text-red-500" : ""} title={t("staffActivities.card.due")}>
            <i className="ri-calendar-event-line mr-0.5" />
            {shortDate(a.dueDate)}
            {overdue && ` · ${t("staffActivities.kanban.overdue")}`}
          </span>
        )}
        {stuck && (
          <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 font-medium text-amber-600" data-testid={`activity-stuck-${a.id}`}>
            <i className="ri-pause-circle-line mr-0.5" />
            {t("staffActivities.kanban.stuck", { days: stuckDays(a, now) ?? 0 })}
          </span>
        )}
      </div>
      {progress != null && progress > 0 && (
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-subtle" title={t("staffActivities.card.estimate")}>
          <div className={`h-full rounded-full ${progress >= 1 ? "bg-warning" : "bg-violet-500"}`} style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>
      )}
      <div className="mt-2 flex items-center justify-between">
        {a.assigneeName ? (
          <span className="flex items-center gap-1.5 text-[11px]">
            <PersonAvatar name={a.assigneeName} size="xs" />
            <span className={tc.textSecondary}>{a.assigneeName}</span>
          </span>
        ) : (
          <span className={`text-[11px] italic ${tc.textSecondary}`}>{t("staffActivities.kanban.unassigned")}</span>
        )}
        {cost && (
          <span className="text-[11px] font-medium text-emerald-600" data-testid={`activity-cost-${a.id}`} title={`${t("staffActivities.card.costEstimated")}: ${formatMoney(cost.estimated, cost.currency)} · ${t("staffActivities.card.costActual")}: ${formatMoney(cost.actual, cost.currency)}`}>
            <i className="ri-money-dollar-circle-line mr-0.5" />
            {formatMoney(cost.estimated ?? cost.actual, cost.currency)}
          </span>
        )}
      </div>
    </div>
  );
}
